import type { BaseQueryFn, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { AxiosRequestConfig } from "axios";
import { AxiosError } from "axios";
import api from "@/lib/axios";

/**
 * RTK Query base query that goes through the shared axios instance.
 *
 * `fetchBaseQuery` talks to the network directly, which means it never sees the
 * response interceptor in `lib/axios` — so an expired access token was never
 * refreshed for these endpoints, and a `403 account_disabled` never signed the
 * person out. Team and credit calls are exactly the ones a disabled member is
 * likely to make, so they were the endpoints least able to afford skipping it.
 *
 * The returned errors deliberately mirror `fetchBaseQuery`'s contract — a
 * `{ status: number, data }` for an HTTP failure and a `{ status: "FETCH_ERROR",
 * error }` for a transport failure — because `readTeamError` in lib/utils/team.utils
 * reads that shape to pull the `details.code` its confirmation dialogs depend on.
 */
export type AxiosBaseQueryArgs =
  | string
  | {
      url: string;
      method?: AxiosRequestConfig["method"];
      /** Named `body` to match the `fetchBaseQuery` call sites; sent as axios `data`. */
      body?: unknown;
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
    };

/**
 * @param pathPrefix Path the endpoints hang off, e.g. "/api/team". Keep it
 * relative — the axios instance supplies the host from
 * `NEXT_PUBLIC_API_BASE_URL`, so building an absolute prefix here would bypass
 * that and hard-code an origin into every call.
 */
export function axiosBaseQuery(
  pathPrefix: string
): BaseQueryFn<AxiosBaseQueryArgs, unknown, FetchBaseQueryError> {
  return async (args) => {
    const request = typeof args === "string" ? { url: args } : args;

    try {
      const response = await api.request({
        url: `${pathPrefix}${request.url}`,
        method: request.method ?? "GET",
        data: request.body,
        params: request.params,
        headers: request.headers,
      });
      return { data: response.data };
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return {
          error: {
            status: error.response.status,
            data: error.response.data,
          },
        };
      }

      // No response at all: offline, DNS, timeout, or a request the interceptor
      // gave up on. Reported the way fetchBaseQuery reports the same thing.
      const axiosError = error as AxiosError;
      return {
        error: {
          status: axiosError.code === "ECONNABORTED" ? "TIMEOUT_ERROR" : "FETCH_ERROR",
          error: axiosError.message ?? "Request failed",
        },
      };
    }
  };
}
